/**
 * PILLAR 2 — Unified action-confirmation helper.
 *
 * One vocabulary across the platform: every operator action — save,
 * sync, retry, failure — comes through `confirm.*`. The point is to
 * never leave the operator wondering "did that work?". Every confirm
 * carries a checkmark glyph and a human verb.
 *
 * Failures get a retry hook and a recovery hint. We never use
 * "Something went wrong" — every error must say what to do next.
 */
import { toast } from "sonner";

const DEFAULT_DURATION = 2400;

function _success(message, opts = {}) {
  return toast.success(`✓ ${message}`, {
    duration: opts.duration ?? DEFAULT_DURATION,
    ...opts,
  });
}

function _info(message, opts = {}) {
  return toast(message, {
    duration: opts.duration ?? DEFAULT_DURATION,
    ...opts,
  });
}

function _error(message, opts = {}) {
  // Failures must say what to do next.
  const description = opts.recovery || opts.description;
  const action = opts.retry
    ? { label: opts.retryLabel || "Retry", onClick: opts.retry }
    : opts.action || null;
  return toast.error(message, {
    duration: opts.duration ?? 6000,
    description,
    action,
  });
}

export const confirm = {
  // --- saves ---
  measurementSaved: (label) =>
    _success(label ? `Measurement saved · ${label}` : "Measurement saved"),
  measurementRenamed: () => _success("Measurement renamed"),
  measurementDeleted: () => _success("Measurement deleted"),
  markupAdded: (kind) =>
    _success(kind ? `${kind[0].toUpperCase()}${kind.slice(1)} markup added` : "Markup added"),
  markupDeleted: () => _success("Markup deleted"),
  markupUndone: () => _info("Last markup undone"),
  pinAdded: () => _success("Pin added"),
  pinDeleted: () => _success("Pin deleted"),
  calibrationUpdated: () => _success("Calibration updated"),
  controlPointAdded: () => _success("Control point added"),
  benchmarkAdded: () => _success("Benchmark added"),
  alignmentSaved: () => _success("Alignment saved"),
  uploadComplete: (count) =>
    _success(count ? `Upload complete · ${count} page(s) ready` : "Upload complete"),
  revisionAcknowledged: () => _success("Revision acknowledged"),
  sheetCalibrated: () => _success("Sheet calibrated"),
  shareSent: () => _success("Share link sent"),

  // --- sync ---
  syncing: () => _info("Syncing…", { duration: 1500 }),
  synced: () => _success("Synced"),
  offline: () =>
    _info("Offline — changes will sync when reconnected", { duration: 3500 }),

  // --- failures (every one MUST include a recovery hint) ---
  // Iter 179 — Silent-failure elimination.
  // `saveFailed` now accepts `error` (typically an axios error) and
  // extracts the backend's `detail` field, so validation messages like
  // "rectangle requires exactly 2 points" reach the operator instead of
  // being swallowed. Placement flows (markup / pin / measure / asset /
  // calibration) route their catches through this helper — one toast
  // vocabulary, one place to tune tone.
  saveFailed: (what = "Couldn't save", { retry, recovery, error } = {}) => {
    let description = recovery || "Tap retry, or check your connection.";
    let title = what;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      // Backend supplied a human message — surface it verbatim.
      description = detail;
    } else if (Array.isArray(detail) && detail[0]?.msg) {
      // FastAPI validation errors come as a list of {loc, msg}.
      description = detail.map((d) => d.msg).join(" · ");
    } else if (!error?.response && error?.message) {
      // Network / offline — no HTTP response.
      description = "Change queued — will sync when reconnected.";
    }
    if (status === 403) title = `${what} · Not allowed`;
    else if (status === 404) title = `${what} · Sheet or project missing`;
    else if (status === 409) title = `${what} · Conflict`;
    else if (status >= 500) title = `${what} · Server error`;
    return _error(title, {
      recovery: description,
      retry,
      duration: 7000,
    });
  },
  uploadFailed: (recovery = "Open the upload to retry failed pages.", retry) =>
    _error("Upload failed", { recovery, retry }),
  calibrationFailed: (recovery = "Open Calibration Studio to retry.", retry) =>
    _error("Calibration failed", { recovery, retry }),
  needsCalibration: (retry) =>
    _error("Sheet not calibrated", {
      recovery: "Calibrate the sheet before measuring. Kept as a draft — tap retry once calibrated.",
      retry,
    }),
  needsControlPoints: () =>
    _error("No control points", {
      recovery: "Open Survey Control and add at least three control points.",
    }),
  gpsDenied: () =>
    _error("Location access denied", {
      recovery: "Enable location for this browser to use Where Am I.",
    }),
  gpsPoor: (accuracyFt) =>
    _info(
      accuracyFt
        ? `GPS poor · ±${Math.round(accuracyFt)} ft — move to open sky`
        : "GPS poor — move to open sky",
      { duration: 3500 },
    ),
  storageUnsafe: () =>
    _error("Uploads disabled", {
      recovery: "Durable storage isn't configured. Contact your administrator.",
    }),
  generic: (message, opts) => _error(message, opts),

  // Raw passthrough — for callers that need a specific shape.
  raw: toast,
};

export default confirm;
